import { Cart } from '@lambda/commons/apis/shopify/types/storefront';
import {
  REEBELO_CURRENCY,
  REEBELO_DOMAINS,
  ReebeloConditions,
  ReebeloCurrencies,
} from '@lambda/reebelo';
import { CheckoutT, CustomerOrderT } from '@lambda/apis/src/checkout/types';
import {
  CheckoutEventProperties,
  CheckoutTrackRequest,
  ProductEventProperties,
} from './types';
import { makeProductProperties } from './trackProduct';
import settings from '@/settings';
import { logger } from '../logger';
import { analytics, shouldTrack, trackEvent } from './vendor/segment/segment';
import { SegmentEvent } from './events';
import { OffersT } from '../checkout/types';

const getOrderId = (cart: Cart): string => {
  try {
    return cart.id.split('/').slice(-1)[0].split('?')[0];
  } catch (e: any) {
    return cart.id;
  }
};

const makeFromThankYou = (
  checkout: CheckoutT,
  order: CustomerOrderT,
  offers: OffersT,
): CheckoutEventProperties => {
  const value = Number(order.grandTotal || 0);
  const discount = Number(order.discountTotal || 0);
  const coupon = order.discounts?.map((d) => d.code).join(', ');

  const products: ProductEventProperties[] = order.lineItems.map((e) => {
    const key = `${e.variantId}`;
    const offer = offers[key];

    return {
      variant_id: String(e.variantId),
      psku: offer?.psku,
      condition: offer?.condition as ReebeloConditions,
      category: offer?.categories?.length ? offer.categories[0] : undefined,
      brand: offer?.brand,
      color: offer?.color,
      name: e.title,
      price: e.price / 100,
      vendor: e.vendorId, // Vendor not provided
      currency: order.currency as ReebeloCurrencies,
      quantity: e.quantity,
      store: settings.store,
      offer_pk: offer?.reebeloPk,
    };
  });

  const phone = order.shippingAddress?.phone || order.billingAddress?.phone;

  return {
    store: settings.store,
    order_id: String(order.id),
    checkout_id: checkout.id,
    email: order.email,
    phone,
    discount,
    coupon,
    value,
    currency: order.currency as ReebeloCurrencies,
    products,
  };
};

const makeFromCart = (cart: Cart): CheckoutEventProperties => {
  const products = cart.lines.edges.map((e) =>
    makeProductProperties({
      variant: e.node.merchandise,
      variantAttributes: e.node.attributes,
    }),
  );

  return {
    store: settings.store,
    order_id: getOrderId(cart),
    currency: REEBELO_CURRENCY[settings.store],
    products,
  };
};

export const makeFromCheckout = (
  checkout: CheckoutT,
): CheckoutEventProperties => {
  const discount =
    (checkout?.discounts?.reduce((acc, d) => acc + d.amount, 0) || 0) / 100;

  const value = checkout.grandTotal / 100;

  const coupon = checkout?.discounts?.map((d) => d.code).join(', ');

  const phone =
    checkout.billingAddress?.phone || checkout.shippingAddress?.phone;

  const products: ProductEventProperties[] = checkout.lineItems.map((e) => ({
    offer_id: e.offerId,
    variant_id: e.variantId,
    sku: e.sku,
    psku: e.psku,
    name: e.title,
    category: e.categoryName,
    price: (e.price ?? 0) / 100,
    compare_price: (e.compareAtPrice ?? 0) / 100,
    currency: checkout.currency as ReebeloCurrencies,
    quantity: e.quantity,
    images: [e.image ?? ''],
    store: checkout.shopifyStore,
    vendor: e.vendorId?.split('#')[1],
  }));

  return {
    store: settings.store,
    checkout_id: checkout.id,
    order_id: checkout.orderId ?? checkout.shopifyOrderId,
    email: checkout.email,
    phone,
    discount,
    coupon,
    value,
    tax: checkout.taxTotal / 100,
    duties: checkout.dutiesTotal / 100,
    currency: checkout.currency as ReebeloCurrencies,
    products,
  };
};

const makeFromOrder = (order: CustomerOrderT): CheckoutEventProperties => {
  const value = Number(order.grandTotal || 0);
  const discount = Number(order.discountTotal || 0);
  const coupon = order.discounts?.map((d) => d.code).join(', ');

  const products: ProductEventProperties[] = order.lineItems.map((e) => ({
    variant_id: String(e.variantId),
    name: e.title,
    price: e.price,
    vendor: e.vendorId, // Vendor not provided
    currency: order.currency as ReebeloCurrencies,
    quantity: e.quantity,
    store: settings.store,
  }));

  const phone = order.shippingAddress?.phone || order.billingAddress?.phone;

  return {
    store: settings.store,
    order_id: String(order.id),
    email: order.email,
    phone,
    discount,
    coupon,
    value,
    currency: order.currency as ReebeloCurrencies,
    products,
  };
};

const makeCheckoutProperties = (
  r: CheckoutTrackRequest,
): CheckoutEventProperties => {
  const { cart, checkout, order, offers } = r;

  if (checkout && order && offers)
    return makeFromThankYou(checkout, order, offers);

  if (cart && checkout)
    return { ...makeFromCart(cart), ...makeFromCheckout(checkout) };

  if (order) return makeFromOrder(order);

  if (cart) return makeFromCart(cart);

  if (checkout) return makeFromCheckout(checkout);

  return {};
};

export const trackCheckoutEvent = (
  event: SegmentEvent,
  r: CheckoutTrackRequest,
): void => {
  try {
    logger.info({ event, r }, 'tracking checkout event');

    const properties = { ...makeCheckoutProperties(r), ...r.properties };

    trackEvent(event, properties);
  } catch (e: any) {
    logger.error({ event, r }, 'error tracking checkout event');
  }
};

export const trackCheckoutStarted = (r: CheckoutTrackRequest) =>
  trackCheckoutEvent(SegmentEvent.CHECKOUT_STARTED, r);

export const trackPaymentInfoEntered = (r: CheckoutTrackRequest) =>
  trackCheckoutEvent(SegmentEvent.CHECKOUT_PAYMENT_INFO_ENTERED, r);

export const trackPaymentSubmitted = (r: CheckoutTrackRequest) =>
  trackCheckoutEvent(SegmentEvent.CHECKOUT_PAYMENT_SUBMITTED, r);

export const trackCheckoutCompleted = (r: CheckoutTrackRequest) =>
  trackCheckoutEvent(SegmentEvent.CHECKOUT_COMPLETED, r);

export const trackDiscountApplied = (r: CheckoutTrackRequest) =>
  trackCheckoutEvent(SegmentEvent.CHECKOUT_DISCOUNT_APPLIED, r);

export const trackDiscountRemoved = (r: CheckoutTrackRequest) =>
  trackCheckoutEvent(SegmentEvent.CHECKOUT_DISCOUNT_REMOVED, r);

export const trackAbandonedCheckout = (checkout: CheckoutT) => {
  try {
    logger.info({ checkout }, 'tracking abandoned checkout');

    const domain = REEBELO_DOMAINS[checkout?.shopifyStore];
    const url = `https://${domain}/checkout/${checkout.id}`;

    if (!shouldTrack()) {
      logger.info({ checkout }, 'not tracking abandoned checkout');

      return;
    }

    const { email } = checkout;

    analytics.identify({
      email,
      abandoned_checkout_url: url,
    });
  } catch (e: any) {
    logger.error({ checkout }, 'error tracking abandoned checkout');
  }
};

export const clearAbandonedCheckout = () => {
  analytics.identify({ abandoned_checkout_url: null });
};
